<template>
  <fieldset
    class="lcu-form-field"
    :class="[classes, { 'lcu-form-hasIcon': icon }]">
    <label class="lcu-form-label" v-if="label !== ''" :for="id" :class="{
        'lcu-form-sideLabel': sideLabel
      }">
      {{ label }}
      <span v-for="link in links" :key="link.text">
        <a
          class="lcu-label-link"
          :href="link.href"
          :target="link.target"
          v-if="link.href"
          :rel="link.rel">
          {{ link.text }}
        </a>
        <span v-else>{{ link.text }}</span>
      </span>
      <span class="lcu-form-required" v-if="required"></span>
      <tooltip v-if="tooltip"
        :text="tooltip"
        :tag="'span'"
        :position="'top'"
        :classes="['lcu-form-info']">
      </tooltip>
      <span v-if="hint" class="lcu-field-hint">({{ hint }})</span>
    </label>
    <slot />
    <span
      v-if="icon"
      class="lcu-field-icon"
      :class="'lcu-field-icon-' + icon"></span>
    <span v-if="select" class="lcu-field-selectIcon"></span>
  </fieldset>
</template>

<script>
import Tooltip from 'ç/tooltip/Tooltip.vue';

export default {
  name: 'form-field',
  components: {
    Tooltip,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      required: true,
      default: 'Text Input',
    },
    icon: {
      type: String,
      required: false,
      default: '',
    },
    required: {
      type: Boolean,
      required: false,
      default: false,
    },
    select: {
      type: Boolean,
      default: false,
    },
    tooltip: {
      type: String,
      required: false,
    },
    hint: {
      type: String,
      required: false,
    },
    sideLabel: {
      type: Boolean,
      required: false,
      default: false,
    },
    noLabel: {
      type: Boolean,
      required: false,
      default: false,
    },
    links: {
      type: [Array, Boolean],
      required: false,
      default: false,
    },
    classes: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
};
</script>

<style lang="scss">
::placeholder {
  color: rgba($black, .3);
}

.lcu-form-field {
  border: 0;
  margin: 0;
  padding: 0;
  position: relative;
}

.lcu-form-group {
  display: flex;
  flex-direction: column;
  background-color: $off-white;
  border: 1px solid rgba($white, .1);
  margin-bottom: 15px;
  padding: 10px;
  position: relative;
}

.lcu-form-groupInner {
  display: flex;
  flex-direction: column;
}

.lcu-form-break {
  border-bottom: 1px solid rgba($black, .2);
  font-weight: 500;
  margin-bottom: 10px;
  padding-bottom: 10px;
}

@mixin basic-input() {
  background-color: $white;
  font-size: 16px;
  padding: 8px 12px;
  color: $legacy-primary;
  border: 1px solid $page-border;
  width: 100%;

  &:focus, &:focus-visible {
    border-color: $legacy-primary;
    outline: none;
  }

  &:focus {
    border-color: $legacy-primary;
    outline: none;
  }

  &:focus-visible {
    border-color: $legacy-primary;
    outline: none;
  }
}

.lcu-form-inputText {
  @include basic-input;
}

.lcu-form-inputTextarea {
  @include basic-input;
  font-family: inherit;
  min-height: 125px;
}

.lcu-form-select {
  @include basic-input;
  appearance: none;
  color: $legacy-primary;

  &:hover {
    cursor: pointer;
  }
}

.lcu-form-checkbox {
  appearance: none;
  background-color: rgba($black, .20);
  border: 1px solid #a4a4a4;
  float: left;
  margin-left: 0;
  margin-right: 10px;
  padding: 6px;

  &:hover {
    cursor: pointer;
  }

  &:checked {
    background-color: $legacy-primary;
    border-color: $legacy-primary;
  }
}

.lcu-form-inputError {
  background-color: rgba($danger, .15);
  border-color: $danger;

  &:focus {
    border-color: $danger;
  }
}

.lcu-field-selectIcon {
  bottom: 10px;
  color: $legacy-primary;
  font-size: 14px;
  right: 10px;
  pointer-events: none;
  position: absolute;

  &:before {
    display: inline-block;
    content: '\e817';
    font-family: 'lcu';
  }
}

.lcu-login-inputText {
  @include basic-input;
  border: 2px solid transparent;
  font-family: 'raleway-bold';
  font-size: 18px;
}

.lcu-form-hasIcon {

  .lcu-login-inputText {
    padding-left: 40px;
  }
}

.lcu-form-label {
  display: block;
  margin-bottom: 10px;
}

.lcu-field-hint {
  color: #8d8d8d;
  display: inline-block;
  font-size: 14px;
  margin-left: 3px;
  position: relative;
  top: -1px;
}

.lcu-form-sideLabel {
  display: inline-block;
  margin-bottom: 0;
  user-select: none;

  &:hover {
    cursor: pointer;
  }
}

.lcu-field-icon {
  bottom: 7px;
  color: rgba($legacy-primary, .5);
  font-size: 21px;
  left: 7px;
  pointer-events: none;
  position: absolute;

  &:before {
    display: inline-block;
    font-family: 'lcu';
  }
}

.lcu-field-icon-email {

  &:before {
    content: '\e81e';
  }
}

.lcu-field-icon-lock {

  &:before {
    content: '\e81d';
  }
}

.lcu-form-required {
  position: relative;
  top: -2px;

  &:before {
    content: '\e819';
    color: $danger;
    display: inline-block;
    font-family: 'lcu';
    font-size: 8px;
    margin-left: 5px;
  }
}

.lcu-form-error {
  color: $danger;
  display: inline-block;
  padding-top: 10px;
}

.lcu-form-info {
  display: inline-block;
  font-size: 13px;
  margin-left: 3px;

  &:before {
    color: rgba($white, .5);
    content: '\e81a';
    font-family: 'lcu';
  }
}

.lcu-group-title {
  font-size: 17px;
  font-weight: 600;
}

.lcu-form-edit {
  color: rgba($white, .4);
  font-size: 14px;
  position: absolute;
  right: 8px;
  text-decoration: none;
  top: 8px;

  &:hover {
    color: $white;
  }

  &:before {
    content: '\e809';
    display: inline-block;
    font-family: 'lcu';
  }
}

.lcu-form-editCancel {
  opacity: .5;

  &:before {
    content: '\e80a';
  }
}

.lcu-button-row {
  display: flex;
  justify-content: flex-end;
}

.lcu-upload-row {
  display: flex;
  justify-content: flex-start;
}

.lcu-edit-pic {
  width: 100px;
}

.multiselect-option {

  &.is-selected {
    background-color: $gray-lightest;
  }
}

.lcu-login-field {

  .lcu-form-label {
    color: $legacy-primary;
  }

  .lcu-login-inputText {
    background-color: transparent;
    color: $legacy-primary;
    border: 0;
    border-bottom: 2px solid $legacy-primary;
  }

  .lcu-field-icon {
    color: rgba($legacy-primary, .5);
  }

  .lcu-form-checkbox {
    background-color: rgba($black, .10);

    &:checked {
      background-color: $legacy-primary;
      border-color: $legacy-primary;
    }
  }
}

.lcu-label-link {
  color: $legacy-secondary;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}
</style>
